import { z } from 'zod';
/**
 * Authoritative cross-tier schemas. The API and the SPA import from here so
 * request/response shapes can never drift.
 *
 * Conventions:
 *   - amounts: integer cents (signed; positive = debit on journal lines).
 *   - timestamps: ISO-8601 strings on the wire.
 *   - ids: UUID v4 strings.
 *   - tenant scope (`client_id`) is NEVER in a request body or path — it is
 *     derived from the JWT (customer) or X-Client-Id header (staff) by the
 *     tenant middleware.
 */
export const UuidSchema = z.string().uuid();
export const IsoDateSchema = z.string().datetime({ offset: true });
export const IsoDateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
// --- Roles & enums ----------------------------------------------------------
export const UserKindSchema = z.enum(['customer', 'staff']);
export const StaffRoleSchema = z.enum([
    'platform_admin',
    'partner',
    'manager',
    'accountant',
    'reviewer',
]);
export const ClientMemberRoleSchema = z.enum(['owner', 'member', 'accountant', 'bookkeeper']);
export const ClientKindSchema = z.enum(['business', 'individual']);
export const PlanTierSchema = z.enum(['starter', 'professional_plus', 'business_plus']);
export const ClientStatusSchema = z.enum(['active', 'trial', 'past_due', 'frozen', 'canceled']);
export const AccountingBasisSchema = z.enum(['cash', 'accrual']);
export const AccountKindSchema = z.enum([
    'asset',
    'liability',
    'equity',
    'revenue',
    'cost_of_revenue',
    'expense',
]);
export const NormalSideSchema = z.enum(['debit', 'credit']);
export const PeriodStatusSchema = z.enum(['open', 'in_review', 'closed', 'locked']);
export const EntrySourceSchema = z.enum(['ai', 'staff', 'migration', 'customer_api', 'system']);
export const EntryStatusSchema = z.enum(['draft', 'posted']);
export const ProposalKindSchema = z.enum([
    'entry',
    'categorization',
    'reconciliation',
    'adjustment',
]);
export const ProposalStatusSchema = z.enum(['pending', 'approved', 'rejected', 'stale']);
export const DocumentKindSchema = z.enum([
    'bank_statement',
    'credit_card_statement',
    'receipt',
    'vendor_invoice',
    'payroll_register',
    '1099_nec',
    '1099_misc',
    '1099_int',
    '1099_div',
    '1099_b',
    '1099_k',
    '1099_r',
    'w2',
    'k1',
    'brokerage_statement',
    'other',
]);
export const ReportKindSchema = z.enum([
    'income_statement',
    'balance_sheet',
    'cash_flow_statement',
    'personal_pl',
    'net_worth',
    'deductible_expense_summary',
    'trial_balance',
    'gl_detail',
    'aje_log',
    'ar_aging',
    'ap_aging',
    'audit_readiness',
    'tax_handoff',
    'review_trail',
]);
// --- Users & clients --------------------------------------------------------
export const UserSchema = z.object({
    id: UuidSchema,
    kind: UserKindSchema,
    email: z.string().email(),
    fullName: z.string().nullable(),
    mfaEnrolled: z.boolean(),
    staffRole: StaffRoleSchema.nullable(),
    createdAt: IsoDateSchema,
});
// Supported jurisdictions + currencies. US is the default; UK/Canada/Nigeria
// are opt-in (each client keeps a single books currency).
export const CountryCodeSchema = z.enum(['US', 'GB', 'CA', 'NG']);
export const CurrencyCodeSchema = z.enum(['USD', 'GBP', 'CAD', 'NGN']);
/** A country's default books currency (used to default the onboarding selector). */
export const CURRENCY_BY_COUNTRY = {
    US: 'USD',
    GB: 'GBP',
    CA: 'CAD',
    NG: 'NGN',
};
/** Country selector options (display order + label), for onboarding. */
export const COUNTRY_OPTIONS = [
    { value: 'US', label: 'United States' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'CA', label: 'Canada' },
    { value: 'NG', label: 'Nigeria' },
];
export const ClientSchema = z.object({
    id: UuidSchema,
    kind: ClientKindSchema,
    legalName: z.string().min(1).max(200),
    displayName: z.string().min(1).max(200),
    country: CountryCodeSchema,
    currency: CurrencyCodeSchema,
    stateCode: z.string().length(2).nullable(),
    planTier: PlanTierSchema,
    status: ClientStatusSchema,
    createdAt: IsoDateSchema,
});
export const EntityTypeSchema = z.enum([
    // US — each maps to a federal return.
    'c_corp',
    's_corp',
    'partnership',
    'llc',
    'sole_proprietor',
    // Shared common-law codes for UK / Canada / Nigeria (label varies by country).
    'limited_company',
    'plc',
    'llp',
]);
/**
 * Business entity types offered per jurisdiction, in display order, each with
 * the label shown for that country. Single source of truth for both the
 * onboarding picklist and the server's "valid entity type for this country"
 * check. Only US values map to US tax forms; non-US entity type is
 * informational for now. `partnership` + `sole_proprietor` are shared; UK,
 * Canada, and Nigeria reuse `limited_company` / `plc` / `llp` with local labels.
 */
export const ENTITY_TYPES_BY_COUNTRY = {
    US: [
        { value: 'c_corp', label: 'C-Corp — Form 1120' },
        { value: 's_corp', label: 'S-Corp — Form 1120S' },
        { value: 'partnership', label: 'Partnership — Form 1065' },
        { value: 'llc', label: 'LLC Single Member — Sch C' },
        { value: 'sole_proprietor', label: 'Sole Proprietorship — Sch C' },
    ],
    GB: [
        { value: 'limited_company', label: 'Private Limited Company (Ltd)' },
        { value: 'plc', label: 'Public Limited Company (PLC)' },
        { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'sole_proprietor', label: 'Sole Trader' },
    ],
    CA: [
        { value: 'limited_company', label: 'Corporation' },
        { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'sole_proprietor', label: 'Sole Proprietorship' },
    ],
    NG: [
        { value: 'limited_company', label: 'Private Company Ltd by Shares (LTD)' },
        { value: 'plc', label: 'Public Company Ltd by Shares (PLC)' },
        { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'sole_proprietor', label: 'Business Name / Sole Proprietorship' },
    ],
};
/** Valid entity-type values for a country (derived from the picklist above). */
export const entityTypesForCountry = (country) => ENTITY_TYPES_BY_COUNTRY[country].map((o) => o.value);
// Tax ID + state are validated by COUNTRY in the union refine below, not on the
// field itself: US keeps its strict EIN/SSN format and required state; non-US
// clients supply an optional free-text local tax number and no US state. So the
// base fields are permissive here (max-length free text / optional) and the
// country-specific rules live in `ClientCreateSchema.superRefine`.
export const ClientCreateBusinessSchema = z.object({
    kind: z.literal('business'),
    legalName: z.string().min(1).max(200),
    displayName: z.string().min(1).max(200),
    taxId: z.string().max(64).optional(),
    stateCode: z.string().length(2).optional(),
    fiscalYearStart: z.string().regex(/^\d{2}-\d{2}$/),
    basis: AccountingBasisSchema.default('cash'),
    entityType: EntityTypeSchema,
    // Multi-currency: default US/USD so the current (US-only) onboarding is
    // unchanged; the country-aware onboarding UI (Phase 1) sends these explicitly.
    country: CountryCodeSchema.default('US'),
    currency: CurrencyCodeSchema.default('USD'),
});
export const ClientCreateIndividualSchema = z.object({
    kind: z.literal('individual'),
    legalName: z.string().min(1).max(200),
    displayName: z.string().min(1).max(200),
    taxId: z.string().max(64).optional(),
    stateCode: z.string().length(2).optional(),
    country: CountryCodeSchema.default('US'),
    currency: CurrencyCodeSchema.default('USD'),
});
export const ClientCreateSchema = z
    .discriminatedUnion('kind', [ClientCreateBusinessSchema, ClientCreateIndividualSchema])
    .superRefine((val, ctx) => {
    if (val.country === 'US') {
        // US keeps strict tax-ID validation: EIN for a business, SSN/ITIN for an
        // individual. (Required — the field is non-optional for US clients.)
        const taxOk = val.kind === 'business'
            ? /^\d{2}-?\d{7}$/.test(val.taxId ?? '')
            : /^\d{3}-?\d{2}-?\d{4}$/.test(val.taxId ?? '');
        if (!taxOk) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['taxId'],
                message: val.kind === 'business' ? 'EIN must be ## - #######' : 'SSN must be ###-##-####',
            });
        }
        if (!val.stateCode) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['stateCode'],
                message: 'State is required',
            });
        }
    }
    // Entity type must belong to the client's jurisdiction (business only).
    if (val.kind === 'business' && !entityTypesForCountry(val.country).includes(val.entityType)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['entityType'],
            message: `Not a valid entity type for ${val.country}`,
        });
    }
});
// --- Engagement, accounts, periods -----------------------------------------
export const EngagementSchema = z.object({
    id: UuidSchema,
    clientId: UuidSchema,
    fiscalYearStart: z.string().regex(/^\d{2}-\d{2}$/),
    basis: AccountingBasisSchema,
    active: z.boolean(),
    createdAt: IsoDateSchema,
});
export const AccountSchema = z.object({
    id: UuidSchema,
    engagementId: UuidSchema,
    parentId: UuidSchema.nullable(),
    code: z.string().min(1).max(20),
    name: z.string().min(1).max(200),
    kind: AccountKindSchema,
    normalSide: NormalSideSchema,
    taxLineHint: z.string().nullable(),
    isSystem: z.boolean(),
    isArchived: z.boolean(),
});
export const AccountingPeriodSchema = z.object({
    id: UuidSchema,
    engagementId: UuidSchema,
    periodStart: IsoDateOnlySchema,
    periodEnd: IsoDateOnlySchema,
    status: PeriodStatusSchema,
    closedAt: IsoDateSchema.nullable(),
});
// --- Journal entries (double-entry) ----------------------------------------
export const JournalLineSchema = z
    .object({
    accountId: UuidSchema,
    amountCents: z.number().int(), // signed; debits positive, credits negative
    memo: z.string().max(500).optional(),
})
    .strict();
export const JournalEntryProposalSchema = z
    .object({
    description: z.string().min(1).max(1000),
    postedAt: IsoDateSchema,
    lines: z
        .array(JournalLineSchema)
        .min(2)
        .max(50)
        .refine((lines) => lines.reduce((s, l) => s + l.amountCents, 0) === 0, {
        message: 'lines must balance (sum of debits and credits = 0)',
    }),
    sourceDocumentIds: z.array(UuidSchema).optional(),
    sourceBankTransactionIds: z.array(UuidSchema).optional(),
})
    .strict();
/** Create payload for a posted journal entry (same shape as an AI proposal). */
export const JournalEntryCreateSchema = JournalEntryProposalSchema;
// --- Ledger reads -----------------------------------------------------------
export const LedgerLineSchema = z.object({
    /** journal_line.id — required by the reclassify endpoint. */
    id: UuidSchema,
    accountId: UuidSchema,
    accountCode: z.string(),
    accountName: z.string(),
    amountCents: z.number().int(), // signed: debit positive, credit negative
    memo: z.string().nullable(),
});
export const LedgerEntrySchema = z.object({
    id: UuidSchema,
    description: z.string(),
    postedAt: IsoDateSchema,
    source: EntrySourceSchema,
    status: EntryStatusSchema,
    lines: z.array(LedgerLineSchema),
});
export const LedgerEntryListSchema = z.object({
    entries: z.array(LedgerEntrySchema),
    nextCursor: z.string().nullable(),
});
export const AccountListSchema = z.object({
    accounts: z.array(AccountSchema),
});
// --- Reports ----------------------------------------------------------------
export const ReportLineSchema = z.object({
    accountId: UuidSchema,
    code: z.string(),
    name: z.string(),
    amountCents: z.number().int(),
});
/**
 * Income Statement (P&L) for a date range. Three line-item buckets with
 * a Gross Profit subtotal between revenue and operating expenses, so the
 * report mirrors the standard Revenue → Cost of Revenue → Gross Profit
 * → Operating Expenses → Net Income hierarchy.
 */
export const IncomeStatementSchema = z.object({
    from: IsoDateOnlySchema,
    to: IsoDateOnlySchema,
    revenue: z.array(ReportLineSchema),
    costOfRevenue: z.array(ReportLineSchema),
    expenses: z.array(ReportLineSchema),
    totalRevenueCents: z.number().int(),
    totalCostOfRevenueCents: z.number().int(),
    grossProfitCents: z.number().int(),
    totalExpensesCents: z.number().int(),
    netIncomeCents: z.number().int(),
});
/** Trial Balance as of a date — debit/credit columns must net to zero. */
export const TrialBalanceRowSchema = z.object({
    accountId: UuidSchema,
    code: z.string(),
    name: z.string(),
    kind: AccountKindSchema,
    debitCents: z.number().int().nonnegative(),
    creditCents: z.number().int().nonnegative(),
});
export const TrialBalanceSchema = z.object({
    asOf: IsoDateOnlySchema,
    rows: z.array(TrialBalanceRowSchema),
    totalDebitCents: z.number().int().nonnegative(),
    totalCreditCents: z.number().int().nonnegative(),
    balanced: z.boolean(),
});
// --- Dashboard --------------------------------------------------------------
export const DashboardSummarySchema = z.object({
    companyName: z.string(),
    kind: ClientKindSchema,
    planTier: PlanTierSchema,
    basis: AccountingBasisSchema,
    periodLabel: z.string(), // e.g. "May 2026"
    periodStart: IsoDateOnlySchema,
    periodEnd: IsoDateOnlySchema,
    incomeCents: z.number().int(),
    expensesCents: z.number().int(),
    netCents: z.number().int(),
    documentCount: z.number().int().nonnegative(),
    accountCount: z.number().int().nonnegative(),
    entryCount: z.number().int().nonnegative(),
    booksStarted: z.boolean(),
});
// --- Documents --------------------------------------------------------------
export const DocumentSchema = z.object({
    id: UuidSchema,
    kind: DocumentKindSchema.nullable(),
    classificationConfidence: z.number().min(0).max(1).nullable(),
    sha256: z.string().length(64),
    mime: z.string(),
    pages: z.number().int().positive().nullable(),
    filename: z.string().nullable(),
    uploadedAt: IsoDateSchema,
    extractionStatus: z.enum(['pending', 'classified', 'extracted', 'failed', 'rejected']),
    /**
     * Populated only when extractionStatus === 'rejected'. Human-readable text
     * explaining why we refused to extract — typically a mismatch between the
     * user-selected document kind and what the AI saw in the file.
     */
    rejectionReason: z.string().nullable(),
});
/** Accepted upload content types (shared by web + api so they can't drift). */
export const ALLOWED_DOCUMENT_MIME = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/heic',
    'text/csv',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/x-ofx',
    'application/vnd.intu.qfx',
];
export const MAX_DOCUMENT_BYTES = 50 * 1024 * 1024; // 50 MB
const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/, 'sha256 must be 64 lowercase hex chars');
const MimeSchema = z.enum(ALLOWED_DOCUMENT_MIME);
export const DocumentUploadUrlRequestSchema = z
    .object({
    filename: z.string().min(1).max(255),
    mime: MimeSchema,
    sha256: Sha256Schema,
    sizeBytes: z.number().int().positive().max(MAX_DOCUMENT_BYTES),
})
    .strict();
export const DocumentUploadUrlResponseSchema = z.object({
    documentId: UuidSchema,
    alreadyExists: z.boolean(),
    uploadUrl: z.string().optional(),
    expiresInSeconds: z.number().int().positive().optional(),
});
export const DocumentCreateRequestSchema = z
    .object({
    documentId: UuidSchema,
    filename: z.string().min(1).max(255),
    mime: MimeSchema,
    sha256: Sha256Schema,
    kind: DocumentKindSchema.optional(),
})
    .strict();
export const DocumentListSchema = z.object({
    documents: z.array(DocumentSchema),
});
export const DocumentDownloadUrlResponseSchema = z.object({
    url: z.string(),
    expiresInSeconds: z.number().int().positive(),
});
// --- AI proposals -----------------------------------------------------------
export const AiProposalSchema = z.object({
    id: UuidSchema,
    engagementId: UuidSchema,
    kind: ProposalKindSchema,
    confidence: z.number().min(0).max(1),
    model: z.string(),
    status: ProposalStatusSchema,
    payload: z.unknown(), // narrowed at the call site by proposal kind
    sourceDocumentIds: z.array(UuidSchema),
    sourceBankTransactionIds: z.array(UuidSchema),
    decidedBy: UuidSchema.nullable(),
    decidedAt: IsoDateSchema.nullable(),
    createdAt: IsoDateSchema,
});
export const ProposalDecisionSchema = z.object({
    decision: z.enum(['approve', 'reject', 'edit_then_approve']),
    editedPayload: z.unknown().optional(), // only when decision = edit_then_approve
    note: z.string().max(2000).optional(),
});
// --- AI chat ----------------------------------------------------------------
export const AiMessageRequestSchema = z.object({
    conversationId: UuidSchema.optional(),
    content: z.string().min(1).max(10_000),
});
// --- Session / Auth ---------------------------------------------------------
export const SessionRequestSchema = z.object({
    email: z.string().email().max(254),
    // In dev, any non-empty password is accepted; the user is created on first
    // request. In production this body shape is replaced by an Identity Platform
    // ID-token exchange — same endpoint, different request schema (separate route
    // version when we wire it).
    password: z.string().min(1).max(200),
    // Optional — supplied by the signup screen to set the new user's name.
    // Ignored if the user already exists.
    fullName: z.string().min(1).max(200).optional(),
});
export const SessionResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number().int().positive(),
    user: UserSchema,
});
// --- /v1/me -----------------------------------------------------------------
export const MeClientSummarySchema = z.object({
    id: UuidSchema,
    kind: ClientKindSchema,
    displayName: z.string(),
    // The client's books currency — the web threads this into money formatting.
    currency: CurrencyCodeSchema,
    planTier: PlanTierSchema,
    status: ClientStatusSchema,
    role: ClientMemberRoleSchema,
    engagementLetterAccepted: z.boolean(),
});
export const MeResponseSchema = z.object({
    user: UserSchema,
    clients: z.array(MeClientSummarySchema),
});
// --- Onboarding -------------------------------------------------------------
export const OnboardingResponseSchema = z.object({
    client: ClientSchema,
    engagement: EngagementSchema,
    accountsCreated: z.number().int().nonnegative(),
});
// --- Engagement letter ------------------------------------------------------
export const EngagementLetterTemplateSchema = z.object({
    version: z.string(),
    title: z.string(),
    body: z.string(),
    effectiveDate: IsoDateOnlySchema,
});
export const EngagementLetterAcceptRequestSchema = z.object({
    templateVersion: z.string(),
    // Customer types their full legal name as the e-signature.
    signatureFullName: z.string().min(2).max(200),
    accepted: z.literal(true),
});
// --- Referral program -------------------------------------------------------
/** A referral code as captured at sign-up (case-insensitive; stored citext). */
export const ReferralCodeSchema = z
    .string()
    .trim()
    .min(4)
    .max(40)
    .regex(/^[A-Za-z0-9]+$/, 'Referral code must be alphanumeric');
/** Become a referral partner. */
export const ReferralEnrollRequestSchema = z.object({
    displayName: z.string().trim().min(2).max(120),
});
/** Accept the Referral Partner Agreement (mirrors the engagement-letter flow). */
export const ReferralAgreementAcceptRequestSchema = z.object({
    templateVersion: z.string(),
    signatureFullName: z.string().min(2).max(200),
    accepted: z.literal(true),
});
export const EngagementLetterAcceptResponseSchema = z.object({
    documentId: UuidSchema,
    acceptedAt: IsoDateSchema,
    templateVersion: z.string(),
});
// --- Pagination + errors ----------------------------------------------------
export const CursorSchema = z.string().min(1).max(1000);
export const PaginationQuerySchema = z.object({
    cursor: CursorSchema.optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
export const ProblemSchema = z.object({
    type: z.string().url(),
    title: z.string(),
    status: z.number().int(),
    detail: z.string().optional(),
    instance: z.string().optional(),
});
//# sourceMappingURL=schemas.js.map